import { iAitaTestConnectionResult } from "@sdk/external-accounts/external-accounts.service";
import { useSDKActionWithDeps } from "@sdk/sdk.hooks";
import { iExternalAppConfig_AITA } from "@sdk/user-management/user-management.models";
import { Alert, Button, Form, Input, Switch, Tooltip, message } from "antd";
import { HelpUsHelpYou } from "components/help-us-help-you/help-us-help-you";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { selectIntegrationConfig_AITA } from "store/modules/workspace/workspace.selectors";

export const AITAIntegrationConfig = () => {
  const integrationConfig = useSelector(selectIntegrationConfig_AITA);

  const { doAction: completeIntegration, isProcessing: isSaving } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (data: iExternalAppConfig_AITA) =>
          SDK.externalAccounts.completeIntegration({
            type: "AITA",
            data: {
              backendBaseUrl: data.backendBaseUrl,
              aitaWorkspaceId: data.aitaWorkspaceId,
              sharedSecret: data.sharedSecret,
              enabled: data.enabled,
            },
          }),
        successMessage: "Tara AI integration has been saved",
        failureMessage: "Something went wrong",
        actionDependencies: [],
      }),
      [],
    );

  const { doAction: removeIntegration, isProcessing: isRemoving } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => () =>
          SDK.externalAccounts.removeIntegration({
            type: "AITA",
          }),
        successMessage: "Integration has been removed",
        failureMessage: "Something went wrong",
        actionDependencies: [],
      }),
      [],
    );

  const [testResult, setTestResult] =
    useState<iAitaTestConnectionResult | null>(null);

  const { doAction: testConnection, isProcessing: isTesting } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => () => SDK.aita.testConnection(),
        failureMessage: "Failed to test Tara AI connection",
        actionDependencies: [],
        onSuccess: (res: iAitaTestConnectionResult) => {
          setTestResult(res);
        },
      }),
      [],
    );

  const [form] = Form.useForm();

  const onFinish = useCallback(
    (e) => {
      e.preventDefault();
      const process = async () => {
        try {
          await form.validateFields();
          const values = form.getFieldsValue();
          completeIntegration(values as iExternalAppConfig_AITA);
        } catch (e) {
          message.error("Please check your input");
        }
      };
      process();
    },
    [completeIntegration, form],
  );

  const initialValues = (integrationConfig?.config ||
    {}) as iExternalAppConfig_AITA;

  const renderTestResult = () => {
    if (!testResult) {
      return null;
    }
    if (testResult.reachable && testResult.verified) {
      return (
        <Alert
          message="Connected & verified"
          type="success"
          showIcon
          className="mb-4"
        />
      );
    }
    if (testResult.reachable && !testResult.verified) {
      return (
        <Alert
          message={`Reachable but secret rejected (HTTP ${testResult.status})`}
          type="warning"
          showIcon
          className="mb-4"
        />
      );
    }
    return (
      <Alert
        message={`Not reachable — ${testResult.detail || "unknown error"}`}
        type="error"
        showIcon
        className="mb-4"
      />
    );
  };

  return (
    <div className="w-full">
      {integrationConfig && (
        <div className="flex flex-row justify-between items-center mb-4">
          {integrationConfig.config?.enabled ? (
            <Alert
              message="Tara AI integration is active"
              type="success"
              showIcon
              className="flex-1"
            />
          ) : (
            <Alert
              message="Tara AI integration is configured but disabled"
              type="warning"
              showIcon
              className="flex-1"
            />
          )}
          <Tooltip title="Remove Integration">
            <Button
              type="link"
              icon={<i className="ri-delete-bin-line"></i>}
              onClick={() => {
                removeIntegration();
              }}
              loading={isRemoving}
            ></Button>
          </Tooltip>
        </div>
      )}

      {renderTestResult()}

      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        requiredMark={false}
        className="bold-form-labels"
        onFinish={onFinish}
      >
        <Form.Item
          name="backendBaseUrl"
          label="Tara AI Backend URL"
          rules={[
            {
              required: true,
              message: "Please enter the Tara AI backend base URL",
            },
          ]}
        >
          <Input placeholder="https://your-aita-backend.example.com" />
        </Form.Item>
        <Form.Item
          name="aitaWorkspaceId"
          label="Tara AI Workspace ID"
          rules={[
            {
              required: true,
              message: "Please enter the Tara AI workspace id",
            },
          ]}
        >
          <Input placeholder="Workspace ID" />
        </Form.Item>
        <Form.Item
          name="sharedSecret"
          label="Shared Secret"
          rules={[
            {
              required: true,
              message: "Please enter the shared secret",
            },
          ]}
        >
          <Input placeholder="Shared Secret" type="password" />
        </Form.Item>
        <div className="flex flex-row items-center justify-between mb-4">
          <div className="font-bold">Enable Tara AI</div>
          <Form.Item
            name="enabled"
            valuePropName="checked"
            style={{ marginBottom: 0, marginLeft: 5 }}
          >
            <Switch />
          </Form.Item>
        </div>
        <div className="flex flex-row justify-end items-center gap-2">
          {integrationConfig && (
            <Button
              className="font-bold"
              loading={isTesting}
              onClick={() => {
                setTestResult(null);
                testConnection();
              }}
            >
              Test integration
            </Button>
          )}
          <Button
            type="primary"
            className="font-bold"
            loading={isSaving}
            onClick={onFinish}
          >
            Save
          </Button>
        </div>
      </Form>

      <HelpUsHelpYou
        chatMessage="How can we improve the Tara AI Integration for you?"
        emoji="✈️"
        description="Share your workflows with Tara AI and we'll ensure that our integration saves you time."
      />
    </div>
  );
};
